import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dismissBannerMessage, getBannerMessages } from '@lib/api'
import { bannerMessageKeys } from '@lib/keys'
import { useAuth } from './useAuth'

export const useBannerMessages = () => {
  const { user: signedInUser } = useAuth()

  return useQuery(
    bannerMessageKeys.activeForUser(signedInUser?.id ?? 0),
    () => {
      if (signedInUser === null)
        return Promise.reject(
          'Unable to query banner messages.  User not signed in.',
        )

      return getBannerMessages(signedInUser.id)
    },
    { enabled: signedInUser !== null }, // Only fetch if user is signed in
  )
}

export const useDismissBannerMessage = () => {
  const { user: signedInUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation(
    (bannerMessageId: number) => {
      if (signedInUser === null)
        return Promise.reject(
          'Unable to dismiss banner message.  User not signed in.',
        )

      return dismissBannerMessage({ userId: signedInUser.id, bannerMessageId })
    },
    {
      onMutate: async (bannerMessageId) => {
        if (signedInUser === null) return

        // cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(
          bannerMessageKeys.activeForUser(signedInUser.id),
        )

        // snapshot the previous value
        const previousBannerMessages = queryClient.getQueryData<
          BannerMessage[]
        >(bannerMessageKeys.activeForUser(signedInUser.id))

        // optimistically update to the new value
        if (previousBannerMessages) {
          queryClient.setQueryData(
            bannerMessageKeys.activeForUser(signedInUser.id),
            previousBannerMessages.filter((m) => m.id !== bannerMessageId),
          )
        }

        // Return a context object with the snapshotted value
        return { previousBannerMessages }
      },
      onError: (_err, _bannerMessageId, context) => {
        if (signedInUser === null) return

        queryClient.setQueryData(
          bannerMessageKeys.activeForUser(signedInUser.id),
          context?.previousBannerMessages,
        )
      },
      onSettled: () => {
        if (signedInUser === null) return

        void queryClient.invalidateQueries(
          bannerMessageKeys.activeForUser(signedInUser.id),
        )
      },
    },
  )
}

export const useBannerMessagesForContext = (context: BannerMessageContext) => {
  const { data: bannerMessages } = useBannerMessages()

  return (
    bannerMessages?.filter((banner) => banner.contexts.includes(context)) ?? []
  )
}
